

// Helper function to retrieve tokens from localStorage or sessionStorage
const getToken = (key) => {
  if (localStorage.getItem(key)) {
    return { token: localStorage.getItem(key), storage: "localStorage" };
  } else if (sessionStorage.getItem(key)) {
    return { token: sessionStorage.getItem(key), storage: "sessionStorage" };
  }
  return { token: null, storage: null };
};

// Global state for token refresh management
let isRefreshing = false;
let failedQueue = [];
let refreshTimeout = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const resetRefreshState = () => {
  isRefreshing = false;
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
};

// Core token refresh function
const refreshTokens = async () => {
  const { token: refreshToken, storage } = getToken("refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token found.");
  }

  const response = await fetch("/api/v2/token/refresh/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refresh: refreshToken,
    }),
    timeout: 8000,
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const { access, refresh } = await response.json();

  // Save tokens back to the storage they were retrieved from
  if (storage === "localStorage") {
    localStorage.setItem("access_token", access);
    localStorage.setItem("refresh_token", refresh);
  } else if (storage === "sessionStorage") {
    sessionStorage.setItem("access_token", access);
    sessionStorage.setItem("refresh_token", refresh);
  }

  return access;
};

// Handle logout/redirect
const handleAuthFailure = () => {
  localStorage.clear();
  sessionStorage.clear();
  window.dispatchEvent(new Event("storage"));
  window.location.href = "/login";
};

// Main authentication handler that both axios and AI SDK can use
export const handleAuthenticatedRequest = async (makeRequest) => {
  // Get current access token
  const { token: accessToken } = getToken("access_token");
  
  try {
    // Try the request with current token
    const response = await makeRequest(accessToken);
    
    // If successful, return response
    if (response.status !== 401) {
      return response;
    }

    // Handle 401 - token expired
    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        return makeRequest(newToken);
      });
    }

    // Start token refresh process
    isRefreshing = true;

    // Set timeout for refresh process
    refreshTimeout = setTimeout(() => {
      if (isRefreshing) {
        processQueue(new Error("Token refresh timeout"), null);
        handleAuthFailure();
        resetRefreshState();
      }
    }, 10000);

    try {
      // Refresh the token
      const newToken = await refreshTokens();
      
      // Process queued requests
      processQueue(null, newToken);
      
      // Retry original request with new token
      return makeRequest(newToken);
      
    } catch (refreshError) {
      processQueue(refreshError, null);
      handleAuthFailure();
      throw refreshError;
    } finally {
      resetRefreshState();
    }

  } catch (error) {
    // Handle network errors
    throw error;
  }
};

// Utility function to add auth header
export const addAuthHeader = (headers = {}, token) => {
  if (token) {
    return {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return headers;
};

// Export utilities for direct use
export { getToken, handleAuthFailure, refreshTokens };